<template>
    <form class="assign" :theme="theme" @submit.prevent="assign">
        <div class="editing" v-if="assignment">
            This exercise was assigned to {{assignment.course.name}} on {{assignment.assignment_created_date | pretty('long')}}.
        </div>
        <template v-if="chapter">
            <label>Assign To</label>
            <div class="flex flex-align">
                <select type="text" v-model="editable.course_id" class="medium" :class="{error: show_course_duplicate_warning}">
                    <option v-if="courses.length" v-for="course of courses" :value="course.id">{{course.name}}</option>
                    <option v-else disabled>No Courses Available</option>
                </select>
            </div>
            <div class="duplicate-warning" v-if="show_course_duplicate_warning">This exercise has already been assigned to the selected class.</div>
        </template>
        <label>Title</label>
        <input type="text" v-model="editable.assignment_title" />
        <label>Instructions (Optional)</label>
        <textarea v-model="editable.assignment_description"></textarea>
        <label>Due Date</label>
        <datetime-picker v-model="editable.assignment_due_date" id="assignment-due-date" position="top" no-label no-button-now no-clear-button output-format="YYYY-MM-DDTHH:mm:ssZ" format="YYYY-MM-DDTHH:mm:ssZ" />
        <div class="flex" v-if="!editable.assignment_display_date">
            <a class="text-button display-date-button" @click="add_display_date">Add Display Date</a>
        </div>
        <template v-else>
            <label>Display Date</label>
            <datetime-picker v-model="editable.assignment_display_date" id="assignment-due-date" position="top" no-label no-button-now no-clear-button output-format="YYYY-MM-DDTHH:mm:ssZ" format="YYYY-MM-DDTHH:mm:ssZ" />
        </template>
        <div class="flex flex-align assign-options">
            <button type="submit">{{assignment ? 'Save Changes' : 'Assign Exercise'}}</button>
            <div class="ff"></div>
            <div class="text-button red" @click="$emit('close')">Discard {{assignment ? 'Changes' : 'Assignment'}}</div>
        </div>
    </form>
</template>

<script>
    export default {
        name: 'Assign',
        props: {
            chapter: Object,
            assignment: Object
        },
        data(){
            return {
                show_course_duplicate_warning: false,
                editable: {
                    assignment_title: '',
                    assignment_description: '',
                    assignment_due_date: new Date().toISOString(),
                    assignment_display_date: null,
                    assignment_lock_date: null,
                    course_id: null,
                    chapter_id: null
                }
            };
        },
        computed: {
            theme(){
                return this.$store.state.theme.active;
            },
            courses(){
                return this.$store.state.courses.all
                .filter(course => {
                    return course.enrollments.find(enrollment => ['teacher', 'administrator', 'ta'].includes(enrollment.type));
                })
                .filter(course => ['upcoming', 'active', 'template', 'inactive'].includes(course.state));
            },
            product(){
                return this.$store.getters.product;
            }
        },
        methods: {
            add_display_date(){
                this.editable.assignment_display_date = new Date().toISOString();
            },
            assign(){
                this.editable.assignment_due_date = new Date(this.editable.assignment_due_date).toISOString();
                this.editable.assignment_display_date = this.editable.assignment_display_date ? new Date(this.editable.assignment_display_date).toISOString() : null;

                const display = new Date(this.editable.assignment_display_date);
                const due = new Date(this.editable.assignment_due_date);

                if(this.editable.assignment_display_date && display < new Date()){
                    return this.$toasted.error('The display date must be in the future.');
                }

                if(this.editable.assignment_due_date && due < new Date()){
                    return this.$toasted.error('The due date must be in the future.');
                }

                //We need to verify a couple of constraints here. Basically, display date < due date < lock date.

                if(this.editable.assignment_due_date && this.editable.assignment_display_date && due < display){
                    return this.$toasted.error('The due date must be at or after the display date.');
                }

                if(this.assignment){
                    //We are editing an existing assignment.
                    this.$http.put(`/products/${this.product.product_id}/assignments/${this.assignment.assignment_id}`, this.editable)
                    .then(() => {
                        this.$toasted.success('Assignment updated.');
                        this.$emit('close');
                    })
                    .catch(error => this.$toasted.error('There was an error updating this assignment.'));
                }else{
                    //We are creating a new assignment.
                    this.$http.post(`/products/${this.product.product_id}/assignments`, this.editable)
                    .then(() => {
                        this.$toasted.success('Assigned successfully.');
                        this.$emit('close');
                    })
                    .catch(error => this.$toasted.error('There was an error creating this assignment. Please make sure you have not already assigned this exercise to this course.'));
                }
            }
        },
        watch: {
            'editable.course_id': {
                handler(){
                    if(this.chapter){
                        this.show_course_duplicate_warning = false;

                        if(!this.editable.course_id){
                            return;
                        }

                        this.$http.post(`/products/${this.product.product_id}/assignments/check`, this.editable)
                        .then(() => {
                            this.show_course_duplicate_warning = false;
                        })
                        .catch(() => {
                            this.show_course_duplicate_warning = true;
                        });
                    }
                }
            }
        },
        created(){
            if(this.chapter){
                this.editable.assignment_title = this.chapter.chapter_title;
                this.editable.chapter_id = this.chapter.chapter_id;

                if(this.courses.length){
                    this.editable.course_id = this.courses[0].id;
                }
            }else if(this.assignment){
                this.editable.assignment_title = this.assignment.assignment_title;
                this.editable.assignment_description = this.assignment.assignment_description;
                this.editable.assignment_due_date = this.assignment.assignment_due_date;
                this.editable.assignment_display_date = this.assignment.assignment_display_date;
                this.editable.assignment_lock_date = this.assignment.assignment_lock_date;
            }
        }
    }
</script>

<style scoped lang="less">
    @import "~@/assets/less/variables";

    .assign
    {
        background: var(--modal-background-color);
        border-radius: 3px;

        h4
        {
            font-weight: 500;
            padding: 10px;
            font-size: 14px;
            color: @grey;
        }

        .date-time-picker
        {
            max-width: 325px;
            margin: 0;
        }
    }

    form
    {
        padding: 20px;

        label
        {
            margin-top: 20px;

            &:first-child
            {
                margin-top: 0;
            }
        }

        textarea
        {
            min-height: 120px;
        }
    }

    .assign-options
    {
        margin-top: 20px;
    }

    .or
    {
        margin: 0 15px;
        font-size: 14px;
        height: 16px;
        line-height: 16px;
    }

    .display-date-button
    {
        margin-top: 6px;
        font-size: 13px;
        display: block;
    }

    .editing
    {
        border: 1px solid @base;
        border-radius: 3px;
        color: @f;
        padding: 10px;
        font-size: 13px;
        text-align: center;
    }

    .duplicate-warning
    {
        color: @red;
        font-size: 12px;
        margin-top: 8px;
    }
</style>
